import React, { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { getPipelines } from '../../services/api';
import { prefetchPage } from '../../pages/pageLoaders';
import { useModalFocus } from '../ui/focus';
import { NAV, NavItem } from './nav';
import { NavIconSvg } from './NavIcons';

// The app's left sidebar (ADR-49): grouped, expanded by default, collapsible
// to icons (remembered per browser), full height and fixed while the page
// scrolls. Below the md breakpoint it's a drawer opened from the top bar.

const STORAGE_KEY = 'sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
  } catch {
    /* private mode / blocked storage: the choice just isn't remembered */
  }
}

function useBadges() {
  // Same key and data as the Pipelines page, so this shares its cache.
  const { data: pipelines } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => getPipelines().then((r) => r.data),
  });
  const { data: notes } = useNotifications(false);
  const failing = Array.isArray(pipelines)
    ? pipelines.filter((p: any) => p?.enabled && p?.last_run?.status === 'failed').length
    : 0;
  return { failing, unread: notes?.unread_count ?? 0 };
}

const NavList: React.FC<{ collapsed: boolean; onNavigate?: () => void }> = ({ collapsed, onNavigate }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const badges = useBadges();

  const badgeFor = (item: NavItem) => {
    if (item.badge === 'failing' && badges.failing > 0)
      return { n: badges.failing, label: `${badges.failing} failing`, cls: 'bg-[#B42318] text-white' };
    if (item.badge === 'unread' && badges.unread > 0)
      return { n: badges.unread, label: `${badges.unread} unread`, cls: 'bg-accent text-sidebar' };
    return null;
  };

  return (
    <>
      {NAV.filter((g) => !g.adminOnly || user?.is_admin).map((group) => (
        <div key={group.label} role="group" aria-label={group.label} className="mt-3 first:mt-0">
          {!collapsed && (
            <p className="px-3 pb-1 pt-2 text-[11px] uppercase tracking-[0.08em] text-[#8695A8]" aria-hidden="true">
              {group.label}
            </p>
          )}
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const badge = badgeFor(item);
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={onNavigate}
                    onMouseEnter={() => prefetchPage(item.to, queryClient)}
                    onFocus={() => prefetchPage(item.to, queryClient)}
                    title={collapsed ? item.label : undefined}
                    aria-label={collapsed ? (badge ? `${item.label}, ${badge.label}` : item.label) : undefined}
                    className={({ isActive }) =>
                      `relative flex min-h-[44px] items-center gap-3 rounded-lg px-3 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${
                        isActive ? 'bg-sidebar-active font-semibold text-white' : 'text-sidebar-fg hover:bg-white/5 hover:text-white'
                      } ${collapsed ? 'justify-center px-0' : ''}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-accent" aria-hidden="true" />}
                        <NavIconSvg icon={item.icon} />
                        {!collapsed && <span className="flex-1">{item.label}</span>}
                        {badge && (
                          <span
                            className={`rounded-full px-1.5 text-[11px] font-semibold leading-[18px] ${badge.cls} ${collapsed ? 'absolute right-1 top-1 min-w-[18px] text-center' : ''}`}
                            aria-hidden={collapsed ? true : undefined}
                          >
                            {badge.n}
                            {!collapsed && <span className="sr-only"> {badge.label.replace(/^\d+ /, '')}</span>}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
};

const Brand: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <NavLink to="/dashboard" className="mb-3 flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">
    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-accent text-[15px] font-bold text-sidebar">E!</span>
    {!collapsed && (
      <span className="flex flex-col leading-tight">
        <span className="text-[15px] font-semibold">Educate</span>
        <span className="text-xs text-[#E4B24A]">Data Ingestion</span>
      </span>
    )}
    <span className="sr-only">Educate Data Ingestion, go to the Dashboard</span>
  </NavLink>
);

export const Sidebar: React.FC<{ mobileOpen: boolean; onMobileClose: () => void }> = ({ mobileOpen, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const drawer = useRef<HTMLDivElement>(null);
  useModalFocus(mobileOpen, drawer, onMobileClose);

  const toggle = () => {
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  };

  return (
    <>
      {/* Desktop: fixed in place, full height. */}
      <nav
        aria-label="Main"
        className={`sticky top-0 hidden h-screen flex-none flex-col overflow-y-auto bg-sidebar px-3 py-4 md:flex ${collapsed ? 'w-[68px]' : 'w-60'}`}
      >
        <Brand collapsed={collapsed} />
        <NavList collapsed={collapsed} />
        <div className="flex-1" />
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`mt-3 flex min-h-[44px] items-center gap-3 rounded-lg px-3 text-sm text-sidebar-fg hover:bg-white/5 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${collapsed ? 'justify-center px-0' : ''}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M9 4v16" />
            <path d={collapsed ? 'M13 10l2 2-2 2' : 'M15 10l-2 2 2 2'} />
          </svg>
          {!collapsed && <span>Collapse sidebar</span>}
        </button>
      </nav>

      {/* Phone / narrow: a drawer over the page. */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-scrim md:hidden" onMouseDown={(e) => e.target === e.currentTarget && onMobileClose()}>
          <div ref={drawer} role="dialog" aria-modal="true" aria-label="Main menu" className="flex h-full w-64 flex-col overflow-y-auto bg-sidebar px-3 py-4">
            <div className="flex items-start justify-between">
              <Brand collapsed={false} />
              <button
                type="button"
                onClick={onMobileClose}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-sidebar-fg hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <nav aria-label="Main">
              <NavList collapsed={false} onNavigate={onMobileClose} />
            </nav>
          </div>
        </div>
      )}
    </>
  );
};
